import { useEffect, useState } from 'react';
import { Button } from './ui.js';

/**
 * How a person organises one contact: lead source, follow-up date, pipeline, stage, notes
 * and a deal. All optional, all free text where the destination owns the meaning —
 * ContactPilot never invents a pipeline or stage identifier.
 *
 * Saved through the same correction endpoint as every other field, so the contact is
 * checked again and an approval given before no longer applies.
 */
export interface ContactOrganizationValue {
  leadSource?: string | null;
  notes?: string | null;
  followUpOn?: string | null;
  pipeline?: string | null;
  stage?: string | null;
  opportunity?: { name: string | null; value: number | null; currency: string | null } | null;
}

interface Fields { leadSource: string; followUpOn: string; pipeline: string; stage: string; notes: string; dealName: string; dealValue: string; currency: string }

const toFields = (o: ContactOrganizationValue | null | undefined): Fields => ({
  leadSource: o?.leadSource ?? '', followUpOn: o?.followUpOn ?? '', pipeline: o?.pipeline ?? '', stage: o?.stage ?? '',
  notes: o?.notes ?? '', dealName: o?.opportunity?.name ?? '',
  dealValue: o?.opportunity?.value != null ? String(o.opportunity.value) : '', currency: o?.opportunity?.currency ?? 'USD',
});

export function toOrganization(f: Fields): ContactOrganizationValue | null {
  const t = (v: string) => (v.trim() ? v.trim() : null);
  const value = f.dealValue.trim() ? Number(f.dealValue) : null;
  const opportunity = t(f.dealName) || value !== null
    ? { name: t(f.dealName), value, currency: value !== null ? (t(f.currency)?.toUpperCase() ?? null) : null } : null;
  const out = { leadSource: t(f.leadSource), followUpOn: t(f.followUpOn), pipeline: t(f.pipeline), stage: t(f.stage), notes: t(f.notes), opportunity };
  return Object.values(out).some((v) => v !== null) ? out : null;
}

export function OrganizeContact({ value, disabled, saving, onSave }: {
  value: ContactOrganizationValue | null | undefined;
  disabled: boolean;
  saving: boolean;
  onSave: (organization: ContactOrganizationValue | null) => Promise<void>;
}) {
  const [f, setF] = useState<Fields>(() => toFields(value));
  useEffect(() => { setF(toFields(value)); }, [value]);
  const initial = JSON.stringify(toOrganization(toFields(value)));
  const changed = JSON.stringify(toOrganization(f)) !== initial;
  const badValue = f.dealValue.trim() !== '' && !(Number(f.dealValue) >= 0);
  const input = 'mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-2 text-base sm:text-sm';
  const set = (k: keyof Fields) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <fieldset className="border-t border-slate-100 pt-4" data-organize-contact="" disabled={disabled}>
      <legend className="text-sm font-medium text-slate-800">Organize</legend>
      <p className="mt-1 text-xs text-slate-500">Optional. Sent with this contact to your destinations. No CRM needed.</p>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <label className="block min-w-0 text-sm"><span className="text-slate-600">Lead source</span>
          <input className={input} value={f.leadSource} onChange={set('leadSource')} maxLength={100} placeholder="e.g. Trade show" /></label>
        <label className="block min-w-0 text-sm"><span className="text-slate-600">Follow up on</span>
          <input type="date" className={input} value={f.followUpOn} onChange={set('followUpOn')} /></label>
        <label className="block min-w-0 text-sm"><span className="text-slate-600">Pipeline</span>
          <input className={input} value={f.pipeline} onChange={set('pipeline')} maxLength={100} /></label>
        <label className="block min-w-0 text-sm"><span className="text-slate-600">Stage</span>
          <input className={input} value={f.stage} onChange={set('stage')} maxLength={100} /></label>
        <label className="block min-w-0 text-sm"><span className="text-slate-600">Deal name</span>
          <input className={input} value={f.dealName} onChange={set('dealName')} maxLength={200} /></label>
        <div className="grid min-w-0 grid-cols-[1fr_5rem] gap-2">
          <label className="block min-w-0 text-sm"><span className="text-slate-600">Deal value</span>
            <input inputMode="decimal" className={input} value={f.dealValue} onChange={set('dealValue')} aria-invalid={badValue} /></label>
          <label className="block min-w-0 text-sm"><span className="text-slate-600">Currency</span>
            <input className={input} value={f.currency} onChange={set('currency')} maxLength={3} /></label>
        </div>
      </div>
      <label className="mt-3 block min-w-0 text-sm"><span className="text-slate-600">Notes</span>
        <textarea className="mt-1 min-h-20 w-full min-w-0 rounded-md border border-slate-300 p-2 text-base sm:text-sm" value={f.notes} onChange={set('notes')} maxLength={2000} /></label>
      {badValue ? <p className="mt-1 text-xs text-red-700">Deal value must be a number.</p> : null}
      <div className="mt-2">
        <Button className="min-h-11" variant="secondary" disabled={!changed || saving || badValue} onClick={() => void onSave(toOrganization(f))}>
          {saving ? 'Saving…' : 'Save organization'}
        </Button>
        {changed ? <p className="mt-1 text-xs text-slate-500">Saving checks this contact again; approve it after saving.</p> : null}
      </div>
    </fieldset>
  );
}
